import {
  AccountType,
  deleteV1CompaniesCompanyIdMaterialRatesId,
  getV1CompaniesCompanyIdAccounts,
  getV1CompaniesCompanyIdMaterialRates,
  getV1CompaniesCompanyIdMaterialRatesTypeahead,
  MaterialRate_Create,
  MaterialRate_Update,
  patchV1CompaniesCompanyIdMaterialRatesId,
  postV1CompaniesCompanyIdMaterialRates,
} from '@treadinc/horizon-api-spec';
import { useCallback } from 'react';

import { extractPagination, Pagination } from '~services/pagination';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { Account } from '../useAccount/models';
import { MaterialRate, MaterialRateTypeahead } from './models';

interface GetCompanyMaterialRatesParams {
  companyId: string;
  customerId?: string;
  defaultOnly?: boolean;
  materialIds?: string[];
  pagination: Pagination;
  projectIds?: string[];
  siteIds?: string[];
}

interface GetCompanyMaterialRatesTypeaheadParams {
  companyId: string;
  cursor?: string;
  customerId?: string;
  materialId?: string;
  projectId?: string;
  siteId?: string;
  query?: string;
}

interface GetCustomersWithMaterialRatesParams {
  pagination: Pagination;
}

export default function useMaterialRates() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const createMaterialRate = useCallback(
    async (body: MaterialRate_Create) => {
      return await postV1CompaniesCompanyIdMaterialRates({
        path: { 'company-id': companyId },
        body,
      });
    },
    [companyId],
  );

  const deleteMaterialRate = useCallback(
    async (id: string) => {
      return await deleteV1CompaniesCompanyIdMaterialRatesId({
        path: { 'company-id': companyId, id },
      });
    },
    [companyId],
  );

  const getCompanyMaterialRates = useCallback(
    async ({
      companyId,
      customerId,
      defaultOnly,
      materialIds,
      pagination,
      projectIds,
      siteIds,
    }: GetCompanyMaterialRatesParams) => {
      const response = await getV1CompaniesCompanyIdMaterialRates({
        path: { 'company-id': companyId },
        query: {
          'filter[customer_account_id]': customerId,
          'filter[default_only]': defaultOnly,
          'filter[material_ids]': materialIds,
          'filter[project_ids]': projectIds,
          'filter[site_ids]': siteIds,
          'page[after]': pagination.after,
          'page[before]': pagination.before,
          'page[limit]': pagination.limit,
        },
      });

      const data = response.data.data.map((materialRate) => {
        return MaterialRate.parse(materialRate);
      });

      return { data, pagination: extractPagination(response) };
    },
    [],
  );

  const getCompanyMaterialRatesTypeahead = useCallback(
    async ({
      companyId,
      cursor,
      customerId,
      materialId,
      projectId,
      siteId,
      query,
    }: GetCompanyMaterialRatesTypeaheadParams) => {
      const response = await getV1CompaniesCompanyIdMaterialRatesTypeahead({
        path: { 'company-id': companyId },
        query: {
          'filter[customer_account_id]': customerId || undefined,
          'filter[material_id]': materialId || undefined,
          'filter[project_id]': projectId || undefined,
          'filter[site_id]': siteId || undefined,
          'search[site][query]': query || undefined,
          'page[after]': cursor || undefined,
          'page[limit]': 20,
        },
      });

      const data = response.data.data.map((materialRate) => {
        return MaterialRateTypeahead.parse(materialRate);
      });

      return { data, pagination: extractPagination(response) };
    },
    [],
  );

  const getCustomersWithMaterialRates = useCallback(
    async ({ pagination }: GetCustomersWithMaterialRatesParams) => {
      const response = await getV1CompaniesCompanyIdAccounts({
        path: { 'company-id': companyId },
        query: {
          'filter[account_types]': [AccountType.CUSTOMER],
          'filter[material_rates]': true,
          'page[after]': pagination.after,
          'page[before]': pagination.before,
          'page[limit]': pagination.limit,
        },
      });

      const data = response.data.data.map((account) => Account.parse(account));

      return { data, pagination: extractPagination(response) };
    },
    [companyId],
  );

  const updateMaterialRate = useCallback(
    async (id: string, body: MaterialRate_Update) => {
      return await patchV1CompaniesCompanyIdMaterialRatesId({
        path: { 'company-id': companyId, id },
        body,
      });
    },
    [companyId],
  );

  return {
    createMaterialRate,
    deleteMaterialRate,
    getCompanyMaterialRates,
    getCompanyMaterialRatesTypeahead,
    getCustomersWithMaterialRates,
    updateMaterialRate,
  };
}
